/* eslint-disable no-undefined */
import { ISearchAndFilterProduct, ISearchAndFilterProductPrice } from '../../../../types';
import { Product } from '@apptus/esales-api'
import getProductPrice from '../getProductPrice';
import generateTicket from '../handleTickets';
import handleFromUrl from '../../../../../../util/handleFromUrl';
import standardisePrice from '../../../../../../util/standardisePrice';

import { PageMeta } from '../../types';

/**
 * Fallback: Get product tags from Shopify's collection or search product data
 * When Voyado doesn't return ElevateTags, we read from the liquid-generated script tag
 */
const getShopifyProductTags = (productKey: string): string[] => {
  try {
    // Try collection tags first (for collection pages)
    let tagsScript = document.getElementById('collectionProductTags');

    // Fallback to search tags (for search pages)
    if (!tagsScript || !tagsScript.textContent) {
      tagsScript = document.getElementById('searchProductTags');
    }

    if (!tagsScript || !tagsScript.textContent) {
      console.warn('collectionProductTags or searchProductTags script not found in DOM');
      return [];
    }

    const tagsData = JSON.parse(tagsScript.textContent);
    return tagsData[productKey] || [];
  } catch (error) {
    console.warn('Could not parse product tags:', error);
    return [];
  }
}

interface MapProductOptions {
  index: number;
  pageIndex: number;
  pageMeta: string | PageMeta;
  recommendationType?: string;
  contextualPriceData?: {
    price: { amount: string, currencyCode: string },
    compareAtPrice: { amount: string, currencyCode: string } | null,
    priceRange: any,
    tags?: string[]
  };
  isFresh?: boolean;
}

const mapProduct = (
  product: Product,
  options: MapProductOptions
): ISearchAndFilterProduct => {
  const { index, pageIndex, pageMeta, recommendationType, contextualPriceData, isFresh } = options;
  let metaObj = pageMeta

  if (pageMeta === 'recommendations' || (typeof pageMeta === 'object' && pageMeta !== null && (pageMeta as PageMeta).pageType === 'recommendations')) {
    const pageResourceId = (window as any).ShopifyAnalytics?.meta?.page?.resourceId;
    const isCartPage = window.location.pathname === '/cart' ||
                       (window as any).ShopifyAnalytics?.meta?.page?.pageType === 'cart';

    let resourceId: string;
    if (isCartPage && !pageResourceId) {
      resourceId = 'recommendations-cart';
    } else {
      // Include recommendation type in resourceId to distinguish between different recommendation types on the same page
      // e.g., "recommendations-123-alternatives" vs "recommendations-123-upsell"
      const baseResourceId = `recommendations-${pageResourceId || 'undefined'}`;
      resourceId = recommendationType ? `${baseResourceId}-${recommendationType.toLowerCase()}` : baseResourceId;
    }

    metaObj = {
      pageType: 'recommendations',
      resourceType: 'recommendations',
      resourceId: resourceId
    }
  }


  generateTicket(product, (metaObj as PageMeta), isFresh);

  // Default to Voyado prices
  let sellingPriceMin = product.sellingPrice.min;
  let sellingPriceMax = product.sellingPrice.max;
  let listPriceMin = product.listPrice.min;
  let listPriceMax = product.listPrice.max;

  // If contextual prices are available (from Storefront API), use them instead
  // Note: Storefront API returns strings (e.g. "10.00"), so we parse them to numbers
  if (contextualPriceData) {
    if (contextualPriceData.priceRange) {
      sellingPriceMin = parseFloat(contextualPriceData.priceRange.minVariantPrice.amount);
      sellingPriceMax = parseFloat(contextualPriceData.priceRange.maxVariantPrice.amount);
    } else if (contextualPriceData.price) {
      // Fallback if price range isn't available (shouldn't happen with our query)
      sellingPriceMin = parseFloat(contextualPriceData.price.amount);
      sellingPriceMax = parseFloat(contextualPriceData.price.amount);
    }

    // For compare at price, it's a bit more complex as variants might have different ones
    // We use the first variant's compare at price as a proxy for list price min/max
    // This is an approximation but standard for quick search results
    if (contextualPriceData.compareAtPrice) {
      const cap = parseFloat(contextualPriceData.compareAtPrice.amount);
      listPriceMin = cap;
      listPriceMax = cap;
    } else {
      // If no compare at price, set list price same as selling price (no discount)
      listPriceMin = sellingPriceMin;
      listPriceMax = sellingPriceMax;
    }
  }

  const priceObj: ISearchAndFilterProductPrice = getProductPrice(
    sellingPriceMin,
    sellingPriceMax,
    listPriceMin,
    listPriceMax
  );

  return {
    id: parseInt(product.key),
    pageIndex: pageIndex,
    index: index,
    title: product.title,
    handle: handleFromUrl(product.link),
    tags: (() => {
      // Try Voyado ElevateTags first
      const voyadoTags = (product as any).elevateTags
        ?? (product as any).ElevateTags
        ?? (product as any).tags
        ?? (product as any).custom?.elevateTags
        ?? (product as any).custom?.ElevateTags
        ?? (product as any).typedCustom?.elevateTags
        ?? (product as any).typedCustom?.ElevateTags
        ?? null;

      if (voyadoTags && Array.isArray(voyadoTags) && voyadoTags.length > 0) {
        return voyadoTags;
      }

      if (voyadoTags && typeof voyadoTags === 'string') {
        return [voyadoTags];
      }

      // Dynamic Fallback: Use tags fetched from Storefront API if available
      if (contextualPriceData && contextualPriceData.tags && contextualPriceData.tags.length > 0) {
        return contextualPriceData.tags;
      }

      // Fallback to Shopify product data when Voyado doesn't provide ElevateTags
      return getShopifyProductTags(product.key);
    })(),
    available: product.inStock,
    url: product.link,
    // type: product.type,
    description: product.description,
    // Build image list: skip first only if there are 2+ images and the first is square; otherwise keep it.
    images: (() => {
      const imagesArray = Array.isArray(product.imageInfo?.images) ? product.imageInfo.images : [];
      const firstSources = imagesArray
        .map(img => (img && Array.isArray(img.sources) ? img.sources[0] : null))
        .filter(Boolean) as { url: string; width?: number; height?: number }[];

      const hasMultiple = firstSources.length > 1;
      const firstIsSquare = firstSources[0] && firstSources[0].width && firstSources[0].height
        ? firstSources[0].width === firstSources[0].height
        : false;
      const usable = (hasMultiple && firstIsSquare) ? firstSources.slice(1) : firstSources;

      const mapped = usable.map(img => ({
        src: img.url,
        alt: product.title,
        width: img.width,
        height: img.height
      }));

      if (mapped.length === 0 && product.imageInfo?.thumbnail) {
        return [{
          src: product.imageInfo.thumbnail,
          alt: product.title,
          width: undefined,
          height: undefined
        }];
      }

      return mapped;
    })(),
    price: standardisePrice(sellingPriceMin),
    price_min: standardisePrice(sellingPriceMin),
    price_max: standardisePrice(sellingPriceMax),
    price_varies: sellingPriceMin === sellingPriceMax ? false : true,
    compare_at_price_max: standardisePrice(listPriceMax),
    priceObj: priceObj,
    variants: product.variants.map(variant => ({
      available: variant.inStock,
      compare_at_price: standardisePrice(variant.sellingPrice), // Note: We aren't doing deep variant contextual pricing yet, just main product
      id: parseInt(variant.key),
      inventory_quantity: variant.stockNumber,
      price: standardisePrice(variant.listPrice)
    })),
    ticket: product.ticket
  };
};

export default mapProduct;
